import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';

// O banco diz o que a tela deve mostrar. Nada de `if (categoria === 'show')`
// espalhado no código — a tela só obedece ao perfil devolvido por `perfil_do_evento`.

export type ChaveRecurso = 'encontros' | 'equipes' | 'loja' | 'divulgacao' | 'entrada_franca';

export type ChaveBloco =
  | 'o_que'
  | 'quando_onde'
  | 'participacoes'
  | 'dinheiro'
  | 'ficha'
  | 'pagamento_parcial'
  | 'encontros'
  | 'presencas'
  | 'equipes'
  | 'loja'
  | 'rifa'
  | 'portaria'
  | 'divulgacao';

export interface ChavesPerfil {
  vende_ingressos: boolean;
  usa_equipes: boolean;
  usa_encontros: boolean;
  vende_produtos: boolean;
  so_divulgacao: boolean;
  tem_rifa: boolean;
  entrada_franca: boolean;
}

export type BlocosPerfil = Record<ChaveBloco, boolean>;

export interface RotulosPerfil {
  participacao: string;
  participacoes: string;
  pessoa: string;
  pessoas: string;
  acao: string;
  ponto: string;
  pontos: string;
}

export interface SituacaoPerfil {
  participacoes: number | null;
  perguntas: number | null;
  encontros: number | null;
  tem_capa: boolean;
  tem_recebedor: boolean;
  loja_estado: string | null;
}

export interface RecursoPerfil {
  chave: ChaveRecurso;
  rotulo: string;
  descricao: string;
  ligado: boolean;
  disponivel: boolean;
}

export interface PerfilEvento {
  evento_id?: number;
  categoria: string;
  status: string;
  chaves: ChavesPerfil;
  blocos: BlocosPerfil;
  rotulos: RotulosPerfil;
  situacao: SituacaoPerfil;
  recursos: RecursoPerfil[];
}

// Retorno de `perfil_da_categoria` — o padrão que o assistente de criar usa.
export interface PerfilCategoria {
  categoria: string;
  vende_ingressos: boolean;
  usa_equipes: boolean;
  usa_encontros: boolean;
  vende_produtos: boolean;
  so_divulgacao: boolean;
  entrada_franca: boolean;
  rotulo_participacao: string;
  termo_ponto: string;
  passos_sugeridos: string[];
}

export type EstadoBloco = 'completo' | 'falta' | 'nao_comecou';

// O estado mostrado no canto do cartão, lido de `situacao`.
export function estadoDoBloco(perfil: PerfilEvento, bloco: ChaveBloco): EstadoBloco | null {
  const s = perfil.situacao;
  switch (bloco) {
    case 'o_que':
      return s.tem_capa ? 'completo' : 'nao_comecou';
    case 'quando_onde':
      return 'completo';
    case 'participacoes':
      return (s.participacoes ?? 0) > 0 ? 'completo' : 'nao_comecou';
    case 'dinheiro':
      return s.tem_recebedor ? 'completo' : 'falta';
    case 'ficha':
      return (s.perguntas ?? 0) > 0 ? 'completo' : 'nao_comecou';
    case 'encontros':
      return (s.encontros ?? 0) > 0 ? 'completo' : 'nao_comecou';
    case 'loja':
      return s.loja_estado ? 'completo' : 'nao_comecou';
    case 'rifa':
      return perfil.chaves.tem_rifa ? 'completo' : 'nao_comecou';
    default:
      return null;
  }
}

const ROTULO_LOJA_ESTADO: Record<string, string> = {
  agendada: 'vendas agendadas',
  aberta: 'vendas abertas',
  fechada: 'vendas fechadas',
  encerrada: 'vendas encerradas',
};

// Pequenas correções de texto que chegam prontas do banco (sem alterar dados).
export function descricaoRecurso(descricao: string | null | undefined): string {
  if (!descricao) return '';
  return descricao.replace(/nas pontos de retirada/gi, 'nos pontos de retirada');
}

export function textoLojaEstado(estado: string | null): string {
  if (!estado) return 'ainda não ligada';
  return ROTULO_LOJA_ESTADO[estado] || estado;
}

// Motivo mostrado em uma linha quando um recurso não está disponível para o evento.
export function motivoIndisponivel(recurso: RecursoPerfil, chaves: ChavesPerfil): string {
  if (recurso.chave === 'equipes' && chaves.vende_ingressos) {
    return 'Evento de ingresso não tem equipes de serviço.';
  }
  if (recurso.chave === 'encontros' && chaves.vende_ingressos) {
    return 'Evento de ingresso não tem encontros e datas.';
  }
  if (recurso.chave === 'loja' && chaves.so_divulgacao) {
    return 'Evento só de divulgação não vende fichas.';
  }
  if (recurso.chave === 'divulgacao' && !chaves.so_divulgacao) {
    return 'Este evento já tem inscrição ou ingresso.';
  }
  if (recurso.chave === 'entrada_franca' && chaves.so_divulgacao) {
    return 'Desligue "Só divulgação" para usar a entrada franca.';
  }
  return 'Não disponível para este tipo de evento.';
}

export async function buscarPerfil(eventoId: number): Promise<PerfilEvento> {
  const { data, error } = await supabase.rpc('perfil_do_evento', { p_evento: eventoId });
  if (error) throw error;
  return data as PerfilEvento;
}

export async function buscarPerfilCategoria(categoria: string): Promise<PerfilCategoria> {
  const { data, error } = await supabase.rpc('perfil_da_categoria', { p_categoria: categoria });
  if (error) throw error;
  return data as PerfilCategoria;
}

interface UsePerfilEvento {
  perfil: PerfilEvento | null;
  carregando: boolean;
  erro: string | null;
  recarregar: () => void;
  definirRecurso: (chave: ChaveRecurso, ligar: boolean) => Promise<void>;
}

export function usePerfilEvento(eventoId: number | null): UsePerfilEvento {
  const [perfil, setPerfil] = useState<PerfilEvento | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let ativo = true;
    if (!eventoId || Number.isNaN(eventoId)) {
      setCarregando(false);
      setPerfil(null);
      return () => {
        ativo = false;
      };
    }
    setCarregando(true);
    setErro(null);
    buscarPerfil(eventoId)
      .then((p) => {
        if (ativo) setPerfil(p);
      })
      .catch((e) => {
        if (ativo) setErro(mensagemAmigavel(e));
      })
      .finally(() => {
        if (ativo) setCarregando(false);
      });
    return () => {
      ativo = false;
    };
  }, [eventoId, reload]);

  const recarregar = useCallback(() => setReload((r) => r + 1), []);

  const definirRecurso = useCallback(
    async (chave: ChaveRecurso, ligar: boolean) => {
      if (!eventoId || Number.isNaN(eventoId)) return;
      const { data, error } = await supabase.rpc('definir_recurso', {
        p_evento: eventoId,
        p_recurso: chave,
        p_ligar: ligar,
      });
      if (error) throw error;
      setPerfil(data as PerfilEvento);
    },
    [eventoId],
  );

  return { perfil, carregando, erro, recarregar, definirRecurso };
}